import { fileSha256 } from "@/common/utils/file-sha256";
import { contentTypeFor } from "@/common/utils/upload-content-type";

/** What an upload reservation declares about a file. */
export type DeclaredUploadFile = {
  fileName: string;
  fileSizeBytes: number;
  /** Also the type the upload must send, via `contentTypeFor`. */
  mimeType: string;
  sha256: string | undefined;
};

/**
 * Describe a file for its upload reservation.
 *
 * Signed upload tokens bind only the path, so the server's scan checks the
 * stored object against these values and quarantines any difference. The
 * upload must therefore send the same bytes under the same `contentTypeFor`
 * type — a HEIC the browser types as "" is reserved and stored as image/heic.
 */
export async function declareUploadFile(
  file: File,
): Promise<DeclaredUploadFile> {
  return {
    fileName: file.name,
    fileSizeBytes: file.size,
    mimeType: contentTypeFor(file),
    sha256: await fileSha256(file),
  };
}

/**
 * Declare several files in order. Sequential so a large batch holds one file's
 * bytes in memory at a time while hashing.
 */
export async function declareUploadFiles(
  files: File[],
): Promise<DeclaredUploadFile[]> {
  const declared: DeclaredUploadFile[] = [];
  for (const file of files) {
    declared.push(await declareUploadFile(file));
  }
  return declared;
}
